<template>
	<Popper
		v-click-outside="closePopover"
		class="flex"
		:isVisible="popoverVisible"
		placement="left"
		fixed
		@keyup.escape="closePopover"
	>
		<div v-if="session.labels?.length" class="flex flex-wrap gap-8 mb-8">
			<SessionLabelPill
				v-for="{label} in session.labels"
				:key="label.id"
				:label="label"
				:interactive="!$store.isExtension"
				data-test="applied-label"
				@click="goToFilteredSessionsList(label)"
			/>
		</div>
		<BaseButton
			v-if="canUserEditSession || isLabelsFeatureEnabled"
			ref="labelButton"
			data-test="change-labels-button"
			:class="{
				'text-gray-300 button-dashed rounded-full': true,
				active: popoverVisible,
				'cursor-not-allowed text-gray-400': !isLabelsFeatureEnabled,
			}"
			:disabled="!isLabelsFeatureEnabled"
			@click="togglePopover"
		>
			<template #icon><BaseIcon name="mono/label" /></template>
			<BaseTooltip
				v-if="!isLabelsFeatureEnabled"
				:text="'Upgrade to team plan to add labels'"
				:placement="'top'"
				class="z-50"
			>
				Add labels
			</BaseTooltip>
			<span v-else>Add <template v-if="session.labels?.length">or remove</template> labels </span>
		</BaseButton>
		<template #popup>
			<BaseSearchablePopover
				ref="searchablePopover"
				v-model="search"
				searchPlaceholder="Add labels"
				data-test="labels-menu"
			>
				<menu v-if="$store.isSessionLabelEnabled" class="m-0 p-0 list-none mt-8">
					<template v-if="searchedLabels?.length">
						<li v-for="label in sortedSearchedLabels" :key="label.id">
							<button
								class="popover-link max-w-full overflow-hidden"
								data-test="label-option-button"
								@click="toggleLabel(label)"
							>
								<SessionLabelPill :label="label" class="bg-gray-650" />
								<BaseIcon
									v-if="selectedLabels.some(({id}) => label.id === id)"
									name="mono/check"
									data-test="checkmark"
									class="ml-auto mb-2 text-green-500"
								/>
							</button>
						</li>
					</template>
					<li v-else-if="$store.hasUserBugReporterRole" class="text-gray-400 pl-12 py-8">No labels found</li>
				</menu>
				<template v-if="!$store.hasUserBugReporterRole && !$store.hasUserProductViewerRole" #footer>
					<button
						v-if="search && !hasExactSearchMatch && $store.isSessionLabelEnabled"
						class="popover-link mt-4 space-x-4"
						style="max-width: 200px"
						data-test="create-and-apply-label-button"
						@click="onCreateAndApplyLabel"
					>
						<BaseIcon v-if="isCreatingAndApplyingLabel" name="mono/load" class="w-14 h-14" />
						<BaseIcon v-else name="mono/plus" class="w-14 h-14" />
						<span class="flex-shrink-0">New label:</span>
						<span class="text-gray-100 truncate">"{{ search }}"</span>
					</button>
					<button
						v-else
						data-test="manage-labels-button"
						type="button"
						class="popover-link mt-4"
						@click="openSettings"
					>
						<BaseIcon name="mono/settings" class="mr-8" />
						<span>Manage labels</span>
					</button>
				</template>
			</BaseSearchablePopover>
		</template>
	</Popper>
</template>

<script>
import {storeToRefs} from 'pinia'
import analytics from 'shared/utils/analytics'
import Popper from 'shared/components/Popper.vue'
import SessionLabelPill from 'shared/components/SessionLabelPill.vue'
import {useLabels} from 'shared/composables/useLabels'
import {useSessionStore} from 'shared/stores/session'
import {FEATURE_CODES} from 'shared/constants/featureCodes'

export default {
	name: 'SessionManageLabels',
	components: {
		Popper,
		SessionLabelPill,
	},
	setup() {
		const sessionStore = useSessionStore()
		const {session, canUserEditSession} = storeToRefs(sessionStore)

		const {
			workspaceLabels,
			searchedLabels,
			search,
			hasExactSearchMatch,
			insertSessionsLabels,
			deleteSessionsLabels,
			insertWorkspaceLabel,
		} = useLabels()

		return {
			session,
			canUserEditSession,
			workspaceLabels,
			searchedLabels,
			search,
			hasExactSearchMatch,
			insertSessionsLabels,
			deleteSessionsLabels,
			insertWorkspaceLabel,
		}
	},
	data() {
		return {
			popoverVisible: false,
			closeOnClickOutside: true,
			isCreatingAndApplyingLabel: false,
		}
	},
	computed: {
		selectedLabels() {
			if (!this.session.labels?.length) return []
			return this.session.labels.map(({label}) => label)
		},
		sortedSearchedLabels() {
			return [...this.searchedLabels].sort((label1, label2) => label1.label.localeCompare(label2.label))
		},
		isLabelsFeatureEnabled() {
			return this.$store.hasPlanFeature(FEATURE_CODES.INCLUDE_LABELS)
		},
	},
	watch: {
		popoverVisible(popoverVisible) {
			window.dispatchEvent(new CustomEvent('labelPickerToggled', {detail: popoverVisible ? 'opened' : 'closed'}))
		},
	},
	methods: {
		goToFilteredSessionsList(label) {
			this.$routerHelpers.push(
				`/workspaces/${this.session.workspaceId}/collections/${this.session.collectionId}?filters=label:${label.label}`
			)
		},
		async toggleLabel(label) {
			if (this.selectedLabels.some(({id}) => label.id === id)) {
				await this.deleteLabel(label)
				return
			}
			await this.addLabel(label)
		},
		async onCreateAndApplyLabel() {
			this.isCreatingAndApplyingLabel = true
			try {
				const label = await this.insertWorkspaceLabel({
					labelText: this.search,
					workspaceId: this.session.workspaceId || this.$store.workspace.id, // in LocalSession, the session doesn't have `workspaceId` set right away
				})
				await this.addLabel(label)
			} finally {
				this.isCreatingAndApplyingLabel = false
				this.$refs.searchablePopover?.$refs.searchInput?.focus()
			}
		},
		async addLabel(label) {
			if (this.$store.isExtension) {
				document.dispatchEvent(new CustomEvent('sessionLabelAdded', {detail: {label}}))
				return
			}
			await this.insertSessionsLabels(label, this.session.id)
		},
		async deleteLabel(label) {
			if (this.$store.isExtension) {
				document.dispatchEvent(new CustomEvent('sessionLabelDeleted', {detail: {label}}))
				return
			}
			await this.deleteSessionsLabels(label, this.session.id)
		},
		togglePopover() {
			if (!this.canUserEditSession) return
			if (this.popoverVisible) {
				this.closePopover()
			} else {
				this.popoverVisible = true
			}
			analytics.track('Session Label Button Clicked')
		},
		closePopover() {
			this.search = ''
			this.popoverVisible = false
		},
		openSettings() {
			analytics.track('Settings Button Clicked', {cta: 'Label Menu'})
			this.closePopover()
			this.$routerHelpers.navigateToWorkspaceSettings('general', {
				hash: '#labels-settings',
			})
		},
	},
}
</script>
